import React, { useState, useEffect } from 'react';
import { getToken } from '../../../../../../services/api';
import './EmailTesting.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EmailTesting = ({ templates }) => {
  const [loading, setLoading] = useState(false);
  const [testData, setTestData] = useState({
    to: '',
    templateId: '',
    subject: '',
    customContent: '',
    variables: {
      nome: '',
      precatorios: '',
      conteudo_personalizado: ''
    }
  });
  const [preview, setPreview] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [error, setError] = useState(null);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (sendResult?.success) {
      const timer = setTimeout(() => setSendResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [sendResult]);

  const handleTemplateChange = (templateId) => {
    setTestData(prev => ({ ...prev, templateId }));
    
    // Get template details
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setTestData(prev => ({ 
        ...prev, 
        subject: template.subject || ''
      }));
    }
  };

  const handleVariableChange = (field, value) => {
    setTestData(prev => ({
      ...prev,
      variables: {
        ...prev.variables,
        [field]: value
      }
    }));
  };

  const handlePreview = async () => {
    if (!testData.templateId) {
      setError('Por favor, selecione um template');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/email/test/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          templateId: testData.templateId,
          variables: {
            ...testData.variables,
            conteudo_personalizado: testData.customContent || testData.variables.conteudo_personalizado
          }
        })
      });

      if (!response.ok) {
        throw new Error('Falha ao gerar preview');
      }

      const data = await response.json();
      setPreview(data.preview);
    } catch (err) {
      setError(err.message);
      console.error('Error generating preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    // Validate required fields
    if (!testData.to) {
      setError('Por favor, insira o email do destinatário');
      return;
    }

    if (!testData.templateId) {
      setError('Por favor, selecione um template');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testData.to)) {
      setError('Formato de email inválido');
      return;
    }

    setLoading(true);
    setSendResult(null);
    setError(null);

    try {
      // Prepare variables
      const variables = {
        nome: testData.variables.nome || 'Cliente Teste',
        precatorios: testData.variables.precatorios || 'TESTE-001, TESTE-002',
        conteudo_personalizado: testData.customContent || testData.variables.conteudo_personalizado || ''
      };

      const response = await fetch(`${API_URL}/email/ba/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          to: testData.to,
          templateId: testData.templateId,
          subject: testData.subject,
          variables,
          useCampaignMethod: true // Flag to use same method as campaigns
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Falha ao enviar email de teste');
      }

      setSendResult({
        success: true,
        message: `Email de teste enviado com sucesso para ${testData.to}`,
        details: data.details
      });

      // Clear form after successful send
      setTestData({
        to: '',
        templateId: '',
        subject: '',
        customContent: '',
        variables: {
          nome: '',
          precatorios: '',
          conteudo_personalizado: ''
        }
      });
      setPreview(null);

    } catch (err) {
      setError(err.message);
      console.error('Error sending test email:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-testing-container">
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {sendResult?.success && (
        <div className="alert alert-success">
          <span>{sendResult.message}</span>
          <button onClick={() => setSendResult(null)}>×</button>
        </div>
      )}

      <div className="testing-grid">
        <div className="test-form-section">
          <h3>Configuração do Email de Teste</h3>
          
          <div className="form-group">
            <label>Email do Destinatário *</label>
            <input
              type="email"
              placeholder="exemplo@email.com"
              value={testData.to}
              onChange={(e) => setTestData(prev => ({ ...prev, to: e.target.value }))}
              className="form-control"
            />
            <small className="form-help">Este email receberá o teste usando o mesmo método de envio das campanhas</small>
          </div>

          <div className="form-group">
            <label>Template *</label>
            <select
              value={testData.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="form-control"
            >
              <option value="">Selecione um template</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            <small className="form-help">O mesmo template usado nas campanhas reais</small>
          </div>

          <div className="form-group">
            <label>Assunto do Email</label>
            <input
              type="text"
              placeholder="Assunto do email"
              value={testData.subject}
              onChange={(e) => setTestData(prev => ({ ...prev, subject: e.target.value }))}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Variáveis do Template</label>
            
            <div className="variable-input">
              <label>Nome do Destinatário</label>
              <input
                type="text"
                placeholder="João Silva"
                value={testData.variables.nome}
                onChange={(e) => handleVariableChange('nome', e.target.value)}
                className="form-control"
              />
            </div>

            <div className="variable-input">
              <label>Números dos Precatórios</label>
              <input
                type="text"
                placeholder="PREC-001, PREC-002"
                value={testData.variables.precatorios}
                onChange={(e) => handleVariableChange('precatorios', e.target.value)}
                className="form-control"
              />
            </div>

            <div className="variable-input">
              <label>Conteúdo Personalizado</label>
              <textarea
                placeholder="Mensagem personalizada opcional..."
                value={testData.customContent}
                onChange={(e) => setTestData(prev => ({ ...prev, customContent: e.target.value }))}
                className="form-control"
                rows="4"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="btn btn-secondary"
              onClick={handlePreview}
              disabled={loading || !testData.templateId}
            >
              {loading ? 'Carregando...' : '👁 Visualizar Preview'}
            </button>
            
            <button
              className="btn btn-primary"
              onClick={handleSendTest}
              disabled={loading || !testData.to || !testData.templateId}
            >
              {loading ? 'Enviando...' : '📧 Enviar Email de Teste'}
            </button>
          </div>

          {sendResult?.details && (
            <div className="send-details">
              <h4>Detalhes do Envio</h4>
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className="value success">Enviado com sucesso</span>
              </div>
              <div className="detail-item">
                <span className="label">Destinatário:</span>
                <span className="value">{sendResult.details.recipients?.join(', ')}</span>
              </div>
              <div className="detail-item">
                <span className="label">Template:</span>
                <span className="value">{sendResult.details.templateUsed}</span>
              </div>
              <div className="detail-item">
                <span className="label">Horário:</span>
                <span className="value">{new Date(sendResult.details.timestamp).toLocaleString('pt-BR')}</span>
              </div>
            </div>
          )}
        </div>

        <div className="preview-section">
          <h3>Preview do Email</h3>
          {preview ? (
            <div className="email-preview">
              <div className="preview-header">
                <div className="preview-field">
                  <strong>Assunto:</strong> {preview.subject}
                </div>
              </div>
              <div className="preview-body">
                <iframe
                  srcDoc={preview.html}
                  title="Email Preview"
                  className="preview-iframe"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Selecione um template e clique em "Visualizar Preview" para ver como o email será exibido</p>
              <p className="preview-note">O preview mostra exatamente como o email aparecerá para o destinatário, usando o mesmo método de renderização das campanhas</p>
            </div>
          )}
        </div>
      </div>

      <div className="testing-info">
        <h4>ℹ️ Sobre o Teste de Email</h4>
        <ul>
          <li>Este teste usa exatamente o mesmo método de envio das campanhas reais</li>
          <li>O email será enviado através do SendGrid com as mesmas configurações</li>
          <li>Todas as variáveis do template serão processadas da mesma forma</li>
          <li>Use este teste para verificar a formatação e entrega antes de enviar campanhas</li>
          <li>O email de teste não será rastreado nas estatísticas de campanha</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailTesting;